import React, { useCallback, useRef } from 'react'
import { useState } from 'react'
import { WemoloTypography } from '../../../components/Wemolo'
import styles from './useFocusingRegion.module.css'

export const useFocusingRegion = (areaFocusEnabled: boolean) => {
    const [indicatorPosition, setIndicatorPosition] = useState({ x: 0, y: 0 })
    const [isVisible, setIsVisible] = useState(false)
    const rectangleSize = { width: 50, height: 50 }
    const labelRef = useRef<HTMLDivElement>(null)
    const [labelStyle, setLabelStyle] = useState({
        left: 0,
        top: 0,
        transform: 'translateX(-50%)'
    })

    const calculateNormalizedPoints = (event: React.MouseEvent<HTMLImageElement>) => {
        const img = event.currentTarget
        const rect = img.getBoundingClientRect()

        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        const normalizedX = x / rect.width
        const normalizedY = y / rect.height

        const halfWidthNormalized = rectangleSize.width / 2 / rect.width
        const halfHeightNormalized = rectangleSize.height / 2 / rect.height

        const points = [
            normalizedX - halfWidthNormalized,
            normalizedY - halfHeightNormalized,
            normalizedX + halfWidthNormalized,
            normalizedY + halfHeightNormalized
        ]
        const clampedPoints = points.map((point) => Math.max(0, Math.min(1, point)))

        return clampedPoints
    }

    const handleImageClick = useCallback(
        (event: React.MouseEvent<HTMLImageElement>) => {
            if (!areaFocusEnabled) return

            const img = event.currentTarget
            const rect = img.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top

            setIndicatorPosition({ x, y })
            setIsVisible(true)

            //settimeout so that its done after callstack is processed (indicator, visible)
            setTimeout(() => {
                const label = labelRef.current
                if (!label) return

                const labelRect = label.getBoundingClientRect()

                let left = x
                let top = y - 50
                let transform = 'translateX(-50%)'

                if (top < 0) {
                    top = y + 33
                }

                if (x + labelRect.width / 2 > rect.width) {
                    left = x - 17
                    transform = 'translateX(-100%)'
                }
                if (x - labelRect.width / 2 < 0) {
                    left = x - 33
                    transform = 'translateX(30%)'
                }

                setLabelStyle({ left, top, transform })
            }, 0)

            return calculateNormalizedPoints(event)
        },
        [areaFocusEnabled]
    )

    const focusingRegionJSX = isVisible && areaFocusEnabled && (
        <>
            <div
                ref={labelRef}
                className={styles.label}
                style={{
                    left: `${labelStyle.left}px`,
                    top: `${labelStyle.top}px`,
                    transform: labelStyle.transform
                }}
            >
                <WemoloTypography variant="title-medium" tag="span">
                    Area Focus In Progress
                </WemoloTypography>
            </div>
            <div
                className={styles.indicator}
                style={{
                    left: `${indicatorPosition.x}px`,
                    top: `${indicatorPosition.y}px`,
                    width: `${rectangleSize.width}px`,
                    height: `${rectangleSize.height}px`
                }}
            ></div>
        </>
    )

    return { handleImageClick, focusingRegionJSX, setIsVisible }
}
